// =============================================================================
// components/feed/RichPostText.tsx — crypto-native post body.
//
// Renders post content and turns $CASHTAGS ($BTC, $ETH…) into tappable accent
// links → open the coin. This seeds the trading mental model right in the social
// feed (the research direction): every mention of a coin is one tap from its
// detail. Nav-agnostic: the screen passes onCashtag(symbol). Pure text, no deps.
// =============================================================================
import React, { useMemo, useState } from 'react';
import { Text, StyleSheet, TextStyle, StyleProp } from 'react-native';
import { colors, fonts } from '../../constants/theme';

// A cashtag is $ + 2–10 letters (BTC, ETH, DOGE, SOL…). Word-boundary so it
// doesn't grab "$5" prices or mid-word "$".
const CASHTAG = /\$[A-Za-z]{2,10}\b/g;

type Segment = { text: string; symbol?: string };

function segment(content: string): Segment[] {
  const out: Segment[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  CASHTAG.lastIndex = 0;
  while ((m = CASHTAG.exec(content)) !== null) {
    if (m.index > last) out.push({ text: content.slice(last, m.index) });
    out.push({ text: m[0], symbol: m[0].slice(1).toUpperCase() });
    last = m.index + m[0].length;
  }
  if (last < content.length) out.push({ text: content.slice(last) });
  return out;
}

export default function RichPostText({
  content,
  style,
  onCashtag,
  numberOfLines,
  collapsedLines,
}: {
  content?: string | null;
  style?: StyleProp<TextStyle>;
  onCashtag?: (symbol: string) => void;
  numberOfLines?: number;
  // When set, long bodies collapse to this many lines with a "See more" toggle
  // (a long post like Gio's no longer floods the feed — better scanability).
  collapsedLines?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const parts = useMemo(() => segment(String(content || '')), [content]);
  if (!content) return null;

  const text = String(content);
  const collapsible = typeof collapsedLines === 'number' && collapsedLines > 0;
  // Heuristic (no measure flicker): a phone feed column fits ~42 chars/line;
  // show the toggle when the body clearly overruns the collapsed window, or has
  // more hard line breaks than fit. Good enough to catch the long ones.
  const lineBreaks = (text.match(/\n/g) || []).length;
  const overflows =
    collapsible &&
    (text.length > collapsedLines! * 42 || lineBreaks >= collapsedLines!);
  const effectiveLines =
    collapsible && !expanded ? collapsedLines : numberOfLines;

  return (
    <>
      <Text style={style} numberOfLines={effectiveLines}>
        {parts.map((p, i) =>
          p.symbol && onCashtag ? (
            <Text
              key={i}
              style={styles.cash}
              onPress={() => onCashtag(p.symbol!)}
              accessibilityRole="link"
              accessibilityLabel={`View ${p.symbol}`}
            >
              {p.text}
            </Text>
          ) : (
            <Text key={i}>{p.text}</Text>
          ),
        )}
      </Text>
      {collapsible && (overflows || expanded) && (
        <Text
          style={styles.more}
          onPress={() => setExpanded((v) => !v)}
          accessibilityRole="button"
          accessibilityLabel={expanded ? 'Show less' : 'Show more'}
        >
          {expanded ? 'See less' : 'See more'}
        </Text>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  cash: { color: colors.primary, ...fonts.semibold },
  more: { color: colors.primary, ...fonts.semibold, fontSize: 13, marginTop: 4 },
});
